
// Registry of promo-code offers served by /p/:promoCode/checkout and /p/:promoCode/shipping.
//
// To add a new offer:
//   1. Add the Cleeng offer id to `offer` in Config.js (and dev/Config.js, stage/Config.js).
//   2. Copy cthomas.js to src/components/Promotions/<promocode>.js, adjust the markup and the
//      `promotion` export. The file name IS the promo code in /p/:promoCode/checkout.
// It is picked up automatically - neither this file nor the checkout pages need to change.
//
// Every field except `offerId` is optional and falls back to DEFAULT_PROMOTION.

import React from 'react';
import cfg from '../../Config';

export const DEFAULT_PROMOTION = {
    // Cleeng offer purchased by this promo code.
    offerId: cfg.offer.annually,
    // Offer box rendered above the sign-up / purchase form. null renders nothing.
    OfferBox: null,
    // Copy shown above the register/login form for logged-out visitors.
    signInHeadline: (
         <div className="head-line">
             <p>Let's Get Started<br />Provide Your Email Address and Create a Password</p>
         </div>
    ),
    // Physical fulfillment. When required is false the address form is skipped entirely
    // and the visitor goes straight to payment.
    shipping: {
        required: false,
        includePhone: false,
        requirePhone: false,
    },
    // When false, visitors with an active (or cancelled but still running) subscription
    // are shown the notEligible block instead of the purchase form.
    allowsExistingSubscribers: false,
    // Where to send the visitor after a successful purchase.
    successPath: '/start-watching',
    // Shown when the visitor cannot take this offer.
    notEligible: {
        body: (
            <p className="text-center text-gray">
                There is no special offer available for your account at this time. Please check back later for new offers.
            </p>
        ),
        cta: null,
    },
};

export function OfferBoxSkeleton() {
    return (
        <div class="row gap-3 mt-4 align-items-start text-center mb-4">
            <div class="border rounded p-3 bg-body col-3 options-box mx-auto">
                <div className="skeleton mx-auto mb-3" style={{ width: 150, height: 220 }}></div>
                <div className="skeleton mx-auto mb-2" style={{ width: '80%', height: 14 }}></div>
                <div className="skeleton mx-auto mb-3" style={{ width: '60%', height: 14 }}></div>
                <div className="skeleton mx-auto mb-2" style={{ width: '90%', height: 10 }}></div>
                <div className="skeleton mx-auto mb-2" style={{ width: '90%', height: 10 }}></div>
                <div className="skeleton mx-auto" style={{ width: '70%', height: 10 }}></div>
            </div>
        </div>
    )
}

// A static `import ... from './${code}'` is not legal JavaScript: import declarations are
// hoisted and resolved at build time, so the path must be a string literal. `import('./' + code)`
// would be legal but returns a Promise, and every caller reads the promotion synchronously
// (offerId, shipping rules) during the first render.
//
// require.context is webpack's synchronous answer: it compiles every promotion file in this
// folder into the bundle up front and hands back a map, so lookup by promo code is a plain
// object read and an unknown code falls through to DEFAULT_PROMOTION instead of throwing.
const promotionModules = require.context('./', false, /^\.\/(?!index\.js$)[^/]+\.js$/);

const PROMOTIONS = promotionModules.keys().reduce((registry, path) => {
    const code = path.replace(/^\.\//, '').replace(/\.jsx?$/, '').toLowerCase();
    const { promotion } = promotionModules(path);
    if (promotion) {
        registry[code] = promotion;
    } else {
        console.error(`Promotions/${path} has no named 'promotion' export and was skipped.`);
    }
    return registry;
}, {});

// Returns a fully populated promotion for `promoCode`, falling back to DEFAULT_PROMOTION
// for unknown codes so an unrecognised link still sells the standard annual offer.
export function getPromotion(promoCode) {

    const code = (promoCode || '').toLowerCase();
    const promotion = PROMOTIONS[code] || {};

    return {
        ...DEFAULT_PROMOTION,
        ...promotion,
        code,
        shipping: { ...DEFAULT_PROMOTION.shipping, ...promotion.shipping },
        notEligible: { ...DEFAULT_PROMOTION.notEligible, ...promotion.notEligible },
    };
}

export default PROMOTIONS;
